/**
 * Utility for client-side image compression and resizing.
 * Targets a file size of < 1MB to comply with Bluesky blob limits.
 */

const GIF_SIZE_LIMIT = 900 * 1024; // 900KB

type GifProgressCallback = (label: string, percent: number) => void;

/**
 * Converts a GIF file to a WebM video blob using canvas frame extraction.
 * Used when the GIF exceeds Bluesky's blob size limit (~976KB).
 */
export const gifToWebM = async (gifFile: File, onProgress?: GifProgressCallback): Promise<Blob> => {
  onProgress?.('Reading GIF...', 0);

  const { parseGIF, decompressFrames } = await import('gifuct-js');
  const arrayBuffer = await gifFile.arrayBuffer();
  const gif = parseGIF(arrayBuffer);

  onProgress?.('Decoding frames...', 5);
  const frames = decompressFrames(gif, true);

  if (!frames.length) throw new Error('No frames found in GIF');

  const { width, height } = gif.lsd;

  // Use a supported mime type — prefer vp9, fall back to vp8
  const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
    ? 'video/webm;codecs=vp9'
    : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
    ? 'video/webm;codecs=vp8'
    : 'video/webm';

  // Use captureStream(30) — auto-captures whatever is on canvas at 30fps.
  // captureStream(0) with requestFrame() is unreliable across browsers.
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  const offscreen = document.createElement('canvas');
  offscreen.width = width;
  offscreen.height = height;
  const offCtx = offscreen.getContext('2d')!;

  const stream = canvas.captureStream(30);
  // Request data every 100ms so chunks are flushed during recording
  const recorder = new MediaRecorder(stream, { mimeType });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

  return new Promise((resolve, reject) => {
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: 'video/webm' });
      console.log('[gifToWebM] output blob size:', blob.size, 'chunks:', chunks.length);
      if (blob.size === 0) {
        reject(new Error('GIF conversion produced an empty video. Please try a smaller GIF.'));
        return;
      }
      resolve(blob);
    };
    recorder.onerror = (e) => reject(e);
    recorder.start(100); // flush data every 100ms

    let frameIndex = 0;
    const drawNext = () => {
      if (frameIndex >= frames.length) {
        onProgress?.('Finishing...', 99);
        recorder.stop();
        return;
      }
      const frame = frames[frameIndex];
      const imageData = new ImageData(
        new Uint8ClampedArray(frame.patch),
        frame.dims.width,
        frame.dims.height
      );
      offCtx.putImageData(imageData, frame.dims.left, frame.dims.top);
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(offscreen, 0, 0);

      const percent = Math.round(10 + (frameIndex / frames.length) * 88);
      onProgress?.(`Converting frame ${frameIndex + 1} of ${frames.length}`, percent);

      frameIndex++;
      setTimeout(drawNext, frame.delay || 100);
    };
    drawNext();
  });
};

/**
 * Returns true if the GIF is too large for direct upload and needs conversion.
 */
export const isGifTooLarge = (file: File) =>
  file.type === 'image/gif' && file.size > GIF_SIZE_LIMIT;

export const compressImage = async (file: File, maxWidth = 2000, maxHeight = 2000, quality = 0.8): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      return resolve(file);
    }

    // Always bypass GIFs — canvas rendering destroys animation (only captures first frame)
    if (file.type === 'image/gif') {
      return resolve(file);
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return reject(new Error('Failed to get canvas context'));
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Export to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Target size is 900KB to be safe (Bluesky limit is ~976KB)
              if (blob.size > 900 * 1024 && quality > 0.1) {
                // Recursively compress further if still too large
                resolve(compressImage(file, maxWidth * 0.8, maxHeight * 0.8, quality - 0.1));
              } else {
                resolve(blob);
              }
            } else {
              reject(new Error('Canvas toBlob failed'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};
